import shuffle from 'lodash/shuffle';
import { reorder } from '@atlaskit/pragmatic-drag-and-drop/reorder';
import {
	type Action,
	createContainer,
	createStore,
	createHook,
	createStateHook,
} from '@atlassian/react-sweet-state';
import { PARTICIPANT_STATUS } from '../../common/constants';
import type { ParticipantBaseProps, ParticipantProps } from '../../common/types';

type StandupsState = {
	isActive: boolean;
	currentlyFilteredId: string | null;
	participants: ParticipantProps[];
};

const initialState: StandupsState = {
	isActive: false,
	currentlyFilteredId: null,
	participants: [],
};

const toggleActiveStandup =
	(): Action<StandupsState> =>
	({ setState, getState }) =>
		setState({ isActive: !getState().isActive });

const setParticipants =
	(participants: ParticipantBaseProps[]): Action<StandupsState> =>
	({ setState }) => {
		const participantsWithStatus = participants.map((participant) => ({
			...participant,
			status: PARTICIPANT_STATUS.PARTICIPATING,
		}));

		setState({ participants: participantsWithStatus });
	};

const shuffleParticipants =
	(): Action<StandupsState> =>
	({ setState, getState }) => {
		const state = getState();
		const activeParticipants = activeParticipantsSelector(state);
		const inactiveParticipants = inactiveParticipantsSelector(state);

		setState({ participants: [...shuffle(activeParticipants), ...inactiveParticipants] });
	};

const reorderParticipant =
	({
		startIndex,
		finishIndex,
	}: {
		startIndex: number;
		finishIndex: number;
	}): Action<StandupsState> =>
	({ setState, getState }) =>
		setState({
			participants: reorder({
				list: getState().participants,
				startIndex,
				finishIndex,
			}),
		});

const toggleParticipantStatus =
	(participantId: string): Action<StandupsState> =>
	({ setState, getState }) =>
		setState({
			participants: getState().participants.map((participant) => {
				if (participant.id !== participantId) {
					return participant;
				}

				return {
					...participant,
					status:
						participant.status === PARTICIPANT_STATUS.NOT_PARTICIPATING
							? PARTICIPANT_STATUS.PARTICIPATING
							: PARTICIPANT_STATUS.NOT_PARTICIPATING,
				};
			}),
		});

const toggleFilteredParticipant =
	(participantId: string): Action<StandupsState> =>
	({ setState, getState }) => {
		const currentId = getState().currentlyFilteredId;

		if (participantId === currentId) {
			setState({ currentlyFilteredId: null });
			return;
		}
		setState({ currentlyFilteredId: participantId });
	};

const actions = {
	toggleActiveStandup,
	setParticipants,
	shuffleParticipants,
	reorderParticipant,
	toggleParticipantStatus,
	toggleFilteredParticipant,
};

export const StandupStoreContainer = createContainer();

const standupStore = createStore({
	name: 'JiraRitualsStandupStore',
	initialState,
	actions,
	containedBy: StandupStoreContainer,
});

export const useStandupStore = createHook(standupStore);

const activeParticipantsSelector = (state: StandupsState) =>
	state.participants.filter(
		(participant) => participant.status === PARTICIPANT_STATUS.PARTICIPATING,
	);

export const useActiveParticipants = createStateHook<
	StandupsState,
	typeof actions,
	ReturnType<typeof activeParticipantsSelector>
>(standupStore, {
	selector: activeParticipantsSelector,
});

const inactiveParticipantsSelector = (state: StandupsState) =>
	state.participants.filter(
		(participant) => participant.status === PARTICIPANT_STATUS.NOT_PARTICIPATING,
	);

export const useInactiveParticipants = createStateHook<
	StandupsState,
	typeof actions,
	ReturnType<typeof inactiveParticipantsSelector>
>(standupStore, {
	selector: inactiveParticipantsSelector,
});
